/**
 * <ask-shifah> — guided chat assistant (no app embed, no server).
 * Each answer lives in a <template data-topic="..."> inside the element.
 * Tapping a [data-chat-topic] chip appends the chip text as the customer's message,
 * shows a short typing indicator, then appends a clone of that topic's template.
 */
class AskShifah extends HTMLElement {
  #ac;
  #log;

  connectedCallback() {
    this.#log = this.querySelector('[data-chat-log]');
    if (!this.#log) return;
    this.#ac = new AbortController();
    this.addEventListener('click', (event) => this.#onClick(event), { signal: this.#ac.signal });
    const panel = this.querySelector('[popover]');
    // Teaser bubble shows once per visit; opening the chat also retires it.
    const teaser = this.querySelector('.ask-shifah-teaser');
    try {
      if (sessionStorage.getItem('askShifahTeaser')) teaser?.remove();
      else sessionStorage.setItem('askShifahTeaser', '1');
    } catch (e) { /* storage blocked: CSS timing still hides it */ }
    panel?.addEventListener('toggle', () => teaser?.remove(), { once: true, signal: this.#ac.signal });
    panel?.addEventListener('toggle', (event) => {
      if (event.newState === 'open' && !this.#log.childElementCount) this.#show('menu');
    }, { signal: this.#ac.signal });
    this.#reopenAfterContactForm(panel);
  }

  /** After the chat's contact form posts (native submit + reload), reopen the chat on the "team" answer so the customer sees the thank-you or any error. */
  #reopenAfterContactForm(panel) {
    const human = this.querySelector('template[data-topic="human"]');
    if (!(human instanceof HTMLTemplateElement) || !panel) return;
    const fromChat = location.hash === '#ask-shifah-form';
    const hasOtherContactForm = document.querySelector('textarea[name="contact[body]"]');
    const hasFeedback = human.content.querySelector('[role="status"], [role="alert"]');
    if (!fromChat && !(hasFeedback && !hasOtherContactForm)) return;
    this.#show('human');
    panel.showPopover?.();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #onClick(event) {
    const chip = event.target instanceof Element ? event.target.closest('[data-chat-topic]') : null;
    if (!chip || !this.contains(chip)) return;
    event.preventDefault();
    this.#log.querySelectorAll('[data-chat-chips]').forEach((group) => group.setAttribute('inert', ''));
    this.#appendUser(chip.textContent.trim());
    this.#typeThen(chip.getAttribute('data-chat-topic'));
  }

  #appendUser(text) {
    const bubble = document.createElement('p');
    bubble.className = 'ms-auto max-w-[80%] rounded-[18px] rounded-ee-[4px] bg-accent px-4 py-2.5 text-sm text-on-plum';
    bubble.textContent = text;
    this.#log.append(bubble);
    this.#scroll();
  }

  #typeThen(topic) {
    const typing = document.createElement('p');
    typing.className = 'w-fit rounded-[18px] bg-surface-secondary px-4 py-2.5 text-sm tracking-[0.2em] text-on-surface/60';
    typing.setAttribute('aria-hidden', 'true');
    typing.textContent = '•••';
    this.#log.append(typing);
    this.#scroll();
    setTimeout(() => {
      typing.remove();
      this.#show(topic);
    }, 550);
  }

  #show(topic) {
    const template = this.querySelector(`template[data-topic="${CSS.escape(topic)}"]`);
    if (!(template instanceof HTMLTemplateElement)) return;
    this.#log.append(template.content.cloneNode(true));
    this.#scroll();
  }

  #scroll() {
    this.#log.scrollTop = this.#log.scrollHeight;
  }
}

if (!customElements.get('ask-shifah')) customElements.define('ask-shifah', AskShifah);
